import {
  PlainButton,
  Button,
  MultiSectionLayout,
  Dialog,
} from "@jugl-web/ui-components";
import {
  useTranslations,
  cx,
  useDebouncedCallback,
  useToast,
} from "@jugl-web/utils";
import { useRef, useEffect, useState } from "react";
import { ENTITY_ID_KEY, getStorageItem } from "@jugl-web/utils/storage";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { ModuleInfoProvider } from "@web-src/modules/workspace/providers/ModuleInfoProvider";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ArrowRightWhiteIcon } from "./assets/arrow-right-white.svg";
import { ModuleCard } from "./components/ModuleCard/ModuleCard";
import featureTasksImage from "./assets/feature-tasks.png";
import featureChatsMeetingsImage from "./assets/feature-chats-meetings.png";
import featureFormsImage from "./assets/feature-forms.png";
import featureInvoicesImage from "./assets/feature-invoices.png";
import featureCatalogImage from "./assets/feature-catalog.png";
import featureWhatsappImage from "./assets/feature-whatsapp.png";
import featureTravelLogImage from "./assets/feature-travel-log.png";
import featureCustomersImage from "./assets/feature-customers.png";
import featureDashboardImage from "./assets/feature-dashboard.png";
import featureScheduledReportsImage from "./assets/feature-scheduled-reports.png";
import featureDriveImage from "./assets/feature-drive.png";
import featureAIAgentsImage from "./assets/feature-aiagents.png";
import featureAutomation from "./assets/feature-automation.png";
import featureIntegrations from "./assets/feature-integrations.png";
import featurePeopleImage from "./assets/feature-people.png";
import { ReactComponent as TitleIcon } from "./assets/title-icon.svg";
import confettiAnimation from "./assets/confetti.json";

import featureTasksAnimation from "./assets/feature-tasks.json";
import featureChatsMeetingsAnimation from "./assets/feature-chats-meetings.json";
import featureFormsAnimation from "./assets/feature-forms.json";
import featureInvoicesAnimation from "./assets/feature-invoices.json";
import featureCatalogAnimation from "./assets/feature-catalog.json";
import featureWhatsappAnimation from "./assets/feature-whatsapp.json";
import featureTravelLogAnimation from "./assets/feature-travel-log.json";
import featureCustomersAnimation from "./assets/feature-customers.json";
import featureDashboardAnimation from "./assets/feature-dashboard.json";
import featureScheduledReportsAnimation from "./assets/feature-scheduled-reports.json";
import featureDriveAnimation from "./assets/feature-drive.json";
import featureAIAgentsAnimation from "./assets/feature-aiagents.json";
import featureAutomationAnimation from "./assets/feature-automation.json";
import featureIntegrationsAnimation from "./assets/feature-integrations.json";
import featurePeopleAnimation from "./assets/feature-people.json";
import { ModuleInfoSection } from "./components/ModuleInfoSection/ModuleInfoSection";
import { useFeaturesShowcaseModal } from "./useFeaturesShowcaseModal";
import { useEntityProvider } from "../../providers/EntityProvider/useEntityProvider";

const FeaturesShowcaseModalContent = () => {
  const { isOpen, close, autoShowConfetti } = useFeaturesShowcaseModal();
  const [isLearnMoreOpenedModuleKey, setIsLearnMoreOpenedModuleKey] =
    useState<SubscriptionPlanModuleId | null>(null);
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { me, isAdmin } = useMe();
  const { toast } = useToast();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [showConfetti, setShowConfetti] = useState(false);
  const [reached, setReached] = useState<"start" | "end" | null>("start");
  const { entity, isEntitiesFetching } = useEntityProvider();

  const scrollContainer = useDebouncedCallback(
    (isLeft?: boolean) => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({
          left: isLeft ? -800 : 800,
          behavior: "smooth",
        });
      }
    },
    300,
    true
  );

  const updateReachedState = useDebouncedCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;

      if (scrollLeft <= 0) {
        setReached("start");
      } else if (scrollLeft >= scrollWidth - clientWidth) {
        setReached("end");
      } else {
        setReached(null);
      }
    }
  }, 100);

  useEffect(() => {
    const scrollElement = scrollContainerRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", updateReachedState);
      return () =>
        scrollElement.removeEventListener("scroll", updateReachedState);
    }
    return undefined;
  }, [updateReachedState]);

  const entityId: string | undefined = getStorageItem(ENTITY_ID_KEY);

  const modulesData = {
    "Jugl::Task": {
      title: t({
        id: "workspace-features-showcase.tasks.title",
        defaultMessage: "Tasks",
      }),
      description: t({
        id: "workspace-features-showcase.tasks.description-1",
        defaultMessage: "Create, assign, and manage orders and tasks",
      }),
      image: featureTasksImage,
      animation: featureTasksAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Chat": {
      title: t({
        id: "workspace-features-showcase.chats-meetings.title",
        defaultMessage: "Chats & Meetings",
      }),
      description: t({
        id: "workspace-features-showcase.chats-meetings.description-1",
        defaultMessage: "Chat and connect with teammates everywhere",
      }),
      image: featureChatsMeetingsImage,
      animation: featureChatsMeetingsAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::People": {
      title: t({
        id: "workspace-features-showcase.people.title",
        defaultMessage: "People",
      }),
      description: t({
        id: "workspace-features-showcase.people.description-1",
        defaultMessage: "Organize your team into departments and teams",
      }),
      image: featurePeopleImage,
      animation: featurePeopleAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::OrderForm": {
      title: t({
        id: "workspace-features-showcase.forms.title",
        defaultMessage: "Forms",
      }),
      description: t({
        id: "workspace-features-showcase.forms.description-1",
        defaultMessage: "Create and share custom forms with your clients",
      }),
      image: featureFormsImage,
      animation: featureFormsAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Invoice": {
      title: t({
        id: "workspace-features-showcase.invoices.title",
        defaultMessage: "Invoices",
      }),
      description: t({
        id: "workspace-features-showcase.invoices.description-1",
        defaultMessage: "Professional invoices straight from your orders",
      }),
      image: featureInvoicesImage,
      animation: featureInvoicesAnimation,
      isNew: true,
      isUpcoming: false,
    },
    "Jugl::Inventory": {
      title: t({
        id: "workspace-features-showcase.catalog.title",
        defaultMessage: "Catalog",
      }),
      description: t({
        id: "workspace-features-showcase.catalog.description-1",
        defaultMessage:
          "Manage item details, and availability all in one place",
      }),
      image: featureCatalogImage,
      animation: featureCatalogAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::WhatsappCrm": {
      title: t({
        id: "workspace-features-showcase.whatsApp.title",
        defaultMessage: "WhatsApp",
      }),
      description: t({
        id: "workspace-features-showcase.whatsApp.description-1",
        defaultMessage: "Confirm orders, and share updates to customers",
      }),
      image: featureWhatsappImage,
      animation: featureWhatsappAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::TravelLog": {
      title: t({
        id: "workspace-features-showcase.travelLog.title",
        defaultMessage: "Travel Log",
      }),
      description: t({
        id: "workspace-features-showcase.travelLog.description-1",
        defaultMessage: "Log employee trips with time, and location",
      }),
      image: featureTravelLogImage,
      animation: featureTravelLogAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Clients": {
      title: t({
        id: "workspace-features-showcase.customers.title",
        defaultMessage: "Customers",
      }),
      description: t({
        id: "workspace-features-showcase.customers.description-1",
        defaultMessage: "Store and manage all your client info in one place",
      }),
      image: featureCustomersImage,
      animation: featureCustomersAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Dashboard": {
      title: t({
        id: "workspace-features-showcase.dashboard.title",
        defaultMessage: "Dashboard",
      }),
      description: t({
        id: "workspace-features-showcase.dashboard.description-1",
        defaultMessage: "See everything that matters at a glance",
      }),
      image: featureDashboardImage,
      animation: featureDashboardAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Reports": {
      title: t({
        id: "workspace-features-showcase.reports.title",
        defaultMessage: "Reports",
      }),
      description: t({
        id: "workspace-features-showcase.scheduledReports.description-1",
        defaultMessage: "Detailed reports about tasks, users, and performance",
      }),
      image: featureScheduledReportsImage,
      animation: featureScheduledReportsAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::Drive": {
      title: t({
        id: "workspace-features-showcase.drive.title",
        defaultMessage: "Drive",
      }),
      description: t({
        id: "workspace-features-showcase.drive.description-1",
        defaultMessage: "Upload, manage, and share files across your workspace",
      }),
      image: featureDriveImage,
      animation: featureDriveAnimation,
      isUpcoming: false,
      isNew: false,
    },
    "Jugl::AI_Agents": {
      title: t({
        id: "workspace-features-showcase.ai-workflows.title",
        defaultMessage: "AI Workflows",
      }),
      description: t({
        id: "workspace-features-showcase.ai-workflows.description-1",
        defaultMessage:
          "Your assistant for daily ops Less guesswork, more clarity",
      }),
      image: featureAIAgentsImage,
      animation: featureAIAgentsAnimation,
      isUpcoming: true,
      isNew: false,
    },
    "Jugl::Automation": {
      title: t({
        id: "workspace-features-showcase.automation.title",
        defaultMessage: "Automation",
      }),
      description: t({
        id: "workspace-features-showcase.automation.description-1",
        defaultMessage: "Automate the busywork and focus on what matters",
      }),
      image: featureAutomation,
      animation: featureAutomationAnimation,
      isUpcoming: true,
      isNew: false,
    },
    "Jugl::Integration": {
      title: t({
        id: "workspace-features-showcase.integrations.title",
        defaultMessage: "Integrations",
      }),
      description: t({
        id: "workspace-features-showcase.integrations.description-1",
        defaultMessage: "Connect your favorite tools in one place",
      }),
      image: featureIntegrations,
      animation: featureIntegrationsAnimation,
      isUpcoming: false,
      isNew: false,
    },
  };

  // TODO: refactor this?
  const config: Record<SubscriptionPlanModuleId, boolean> = {
    "Jugl::Task": true,
    "Jugl::Chat": true,
    "Jugl::People": true,
    "Jugl::OrderForm": true,
    "Jugl::Inventory": true,
    "Jugl::WhatsappCrm": true,
    "Jugl::TravelLog": true,
    "Jugl::Clients": true,
    "Jugl::Dashboard": true,
    "Jugl::Reports": true,
    "Jugl::Drive": true,
    "Jugl::Integration": true,
    "Jugl::Invoice": true,
    "Jugl::AI_Agents": true,
    "Jugl::Automation": true,
    "Jugl::Calls": false,
    "Jugl::TimeLog": false,
    "Jugl::Conference": false,
    "Jugl::Task::Sharing": false,
    "Jugl::Workato": false,
  };

  const modules = Object.entries(config).filter(([, value]) => value);

  useEffect(() => {
    const handleLoadingComplete = () => {
      setTimeout(() => {
        setShowConfetti(true);
      }, 500);
      toast(
        t({
          id: "feedback.workspace-created-successfully",
          defaultMessage: "Workspace created successfully",
        }),
        { autoHideDuration: 3000 }
      );
    };
    document.addEventListener(
      "create-workspace-loading-complete",
      handleLoadingComplete
    );
    return () => {
      document.removeEventListener(
        "create-workspace-loading-complete",
        handleLoadingComplete
      );
    };
  }, [me, t, toast]);

  useEffect(() => {
    if (me && autoShowConfetti && entity && !isEntitiesFetching) {
      setTimeout(() => {
        setShowConfetti(true);
      }, 500);
    }
  }, [me, autoShowConfetti, entity, isEntitiesFetching]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        className="z-[1399] h-[626px] w-[1046px] rounded-2xl bg-white shadow-2xl 2xl:h-[783px] 2xl:w-[1310px]"
        onClose={close}
        backdropClassName="backdrop-blur-sm bg-transparent"
      >
        {showConfetti && me && (
          <>
            <Lottie
              options={{
                animationData: confettiAnimation,
                loop: false,
                autoplay: true,
              }}
              isClickToPauseDisabled
              eventListeners={[
                {
                  eventName: "complete",
                  callback: () => setShowConfetti(false),
                },
              ]}
              height={1000}
              width={500}
              style={{
                zIndex: 99999,
                position: "fixed",
                top: "-100px",
                left: "60%",
                pointerEvents: "none",
              }}
            />
            <Lottie
              options={{
                animationData: confettiAnimation,
                loop: false,
                autoplay: true,
              }}
              height={1000}
              width={500}
              isClickToPauseDisabled
              style={{
                transform: "rotate(100deg)",
                zIndex: 99999,
                position: "fixed",
                top: "-100px",
                left: "20%",
                pointerEvents: "none",
              }}
            />
          </>
        )}

        <MultiSectionLayout
          className={cx("overflow-hidden", {
            hidden: isLearnMoreOpenedModuleKey,
          })}
          header={
            <div
              className={cx(
                "border-dark-100 flex h-[60px] w-full items-center justify-between border-0 border-b-[1px] border-solid px-[24px]"
              )}
            >
              <div className="flex h-full items-center gap-3 text-lg font-semibold text-[#383838]">
                <TitleIcon />
                {t({
                  id: "workspace-features-showcase.main-title-2",
                  defaultMessage: "Features",
                })}
              </div>
              <PlainButton
                onClick={() => {
                  close();
                }}
              >
                <CloseIcon />
              </PlainButton>
            </div>
          }
          footer={
            <div className="flex flex-col items-start justify-between pl-[60px] pt-4 pb-4 pr-[64px] lg:flex-row lg:items-center 2xl:pb-[80px]">
              <div>
                <div className="text-dark text-xl font-medium">
                  {isAdmin
                    ? t({
                        id: "workspace-features-showcase.customize-info-title",
                        defaultMessage: "Want to customize further?",
                      })
                    : t({
                        id: "workspace-features-showcase.customize-info-title-non-admin-2",
                        defaultMessage:
                          "You've joined a workspace that's built for success",
                      })}
                </div>
                <div className="flex gap-1 pt-2 text-[#828282]">
                  {isAdmin
                    ? t({
                        id: "workspace-features-showcase.customize-info-description",
                        defaultMessage:
                          "You can enable or disable any module anytime from your",
                      })
                    : t({
                        id: "workspace-features-showcase.customize-info-description-non-admin",
                        defaultMessage:
                          "Everything your team needs to collaborate effectively is right here.",
                      })}
                  {isAdmin && (
                    <div
                      className="text-primary-800 cursor-pointer underline"
                      onClick={() => {
                        close();
                        navigateToPage("workspaceFeatures", undefined, {
                          entityId,
                        });
                      }}
                    >
                      {t({
                        id: "workspace-features-showcase.customize-info-link",
                        defaultMessage: "workspace settings",
                      })}
                    </div>
                  )}
                </div>
              </div>

              <Button
                color="primary"
                className="mt-2 h-[40px] rounded-lg lg:mt-0"
                iconEnd={<ArrowRightWhiteIcon />}
                onClick={() => {
                  close();
                }}
              >
                {t({
                  id: "workspace-features-showcase.explore-workspace",
                  defaultMessage: "Explore Workspace",
                })}
              </Button>
            </div>
          }
        >
          <div
            className={cx(
              "font-secondary showcase-content-container relative h-full w-full overflow-x-hidden py-2 max-lg:justify-center"
            )}
          >
            <div className="flex flex-col justify-between pl-[60px] pt-2 lg:flex-row 2xl:pt-10">
              <div>
                <div className="text-[32px] font-medium text-black">
                  {isAdmin
                    ? t({
                        id: "workspace-features-showcase.title",
                        defaultMessage: "Features to power your workflow 🚀",
                      })
                    : t({
                        id: "workspace-features-showcase.title-non-admin",
                        defaultMessage:
                          "Features to help you hit the ground running 🚀",
                      })}
                </div>
                <div className="pt-2 text-[#828282]">
                  {t({
                    id: "workspace-features-showcase.sub-title",
                    defaultMessage:
                      "We’ve picked the best features to power your workflow from day one.",
                  })}
                </div>
              </div>

              <div className="flex items-center gap-6 pr-[57px] pt-2 lg:pt-0">
                <PlainButton
                  className="border-grey-500 hover:bg-grey-100 flex h-[42px] w-[42px] items-center justify-center rounded-full border-[1.5px] border-solid transition-colors disabled:opacity-50"
                  onClick={() => scrollContainer(true)}
                  disabled={reached === "start"}
                >
                  <ArrowLeftIcon />
                </PlainButton>
                <PlainButton
                  className="border-grey-500 hover:bg-grey-100  flex h-[42px] w-[42px] items-center justify-center rounded-full border-[1.5px] border-solid transition-colors disabled:opacity-50"
                  onClick={() => scrollContainer(false)}
                  disabled={reached === "end"}
                >
                  <ArrowLeftIcon className="rotate-180" />
                </PlainButton>
              </div>
            </div>

            <div className="pl-[60px]">
              <div
                className="jugl__hidden-scrollbar flex gap-[32px] overflow-x-auto pt-4 pr-[32px] 2xl:pt-[32px]"
                ref={scrollContainerRef}
              >
                {modules.map(([key]) => {
                  const module = modulesData[key as keyof typeof modulesData];
                  return (
                    <ModuleCard
                      key={key}
                      title={module.title}
                      isNew={module.isNew}
                      isUpcoming={module.isUpcoming}
                      description={module.description}
                      image={module.image}
                      animation={module.animation}
                      onReadMoreClick={() =>
                        setIsLearnMoreOpenedModuleKey(
                          key as SubscriptionPlanModuleId
                        )
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </MultiSectionLayout>
        <ModuleInfoSection
          initialFeature={isLearnMoreOpenedModuleKey}
          onClose={(closeParentModal) => {
            setIsLearnMoreOpenedModuleKey(null);
            if (closeParentModal) {
              close();
            }
          }}
          config={config}
        />
      </Dialog>
    </>
  );
};

export const FeaturesShowcaseModal = () => {
  const { entity, subscriptionInfo } = useEntityProvider();
  return entity && subscriptionInfo ? (
    <ModuleInfoProvider>
      <FeaturesShowcaseModalContent />
    </ModuleInfoProvider>
  ) : null;
};
